import React, { useState, useEffect } from 'react';
import {
  Popover,
  Box,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import RecommendationScoreBar, { ScoreChip } from './RecommendationScoreBar';
import { formatNumber, formatPercent } from '../../utils/format';

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Score breakdown popover showing detailed scoring components.
 */
export default function ScoreBreakdownPopover({
  sourceProductId,
  targetProductId,
  relationType,
  children,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [breakdown, setBreakdown] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Fetch breakdown when popover opens
  useEffect(() => {
    if (!open || !sourceProductId || !targetProductId) return;

    const fetchBreakdown = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          source: sourceProductId,
          target: targetProductId,
        });
        if (relationType) {
          params.append('relation_type', relationType);
        }

        const response = await fetch(`${API_BASE}/api/relations/explain?${params}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const data = await response.json();
        setBreakdown(data);
      } catch (err) {
        console.error('Failed to fetch score breakdown:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBreakdown();
  }, [open, sourceProductId, targetProductId, relationType]);

  return (
    <>
      {/* Trigger element */}
      <Box
        onClick={handleOpen}
        sx={{ cursor: 'pointer', display: 'inline-flex' }}
      >
        {children || (
          <Tooltip title="View score breakdown">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              maxWidth: 400,
              p: 2,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Score Breakdown
          </Typography>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {breakdown && !loading && (
          <ScoreBreakdownContent breakdown={breakdown} />
        )}
      </Popover>
    </>
  );
}

/**
 * Score breakdown content component.
 */
function ScoreBreakdownContent({ breakdown }) {
  const {
    final_score,
    base_scores = {},
    boosts = {},
    weights = {},
    relation_type,
    override,
    learned_bias = {},
  } = breakdown;

  // Core scores
  const coreScores = [
    { key: 'match_score', label: 'Match Score', value: base_scores.match_score },
    { key: 'upsell_score', label: 'Upsell Score', value: base_scores.upsell_score },
    { key: 'competitor_score', label: 'Competitor Score', value: base_scores.competitor_score },
    { key: 'co_purchase_score', label: 'Co-Purchase', value: base_scores.co_purchase_score },
    { key: 'cf_score', label: 'Collaborative Filter', value: base_scores.cf_score },
    { key: 'cb_score', label: 'Content-Based', value: base_scores.cb_score },
    { key: 'kg_score', label: 'Knowledge Graph', value: base_scores.kg_score },
  ].filter(s => s.value !== undefined && s.value !== null);

  // Boost factors
  const boostItems = [
    { key: 'margin_boost', label: 'Margin Boost', value: boosts.margin_boost },
    { key: 'seasonal_boost', label: 'Seasonal Boost', value: boosts.seasonal_boost },
    { key: 'persona_boost', label: 'Persona Boost', value: boosts.persona_boost },
    { key: 'tag_similarity', label: 'Tag Similarity', value: boosts.tag_similarity },
    { key: 'pair_stats_boost', label: 'Pair Stats', value: boosts.pair_stats_boost },
  ].filter(b => b.value !== undefined && b.value !== null && b.value !== 0);

  return (
    <Stack spacing={2}>
      {/* Final Score */}
      <Box>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Final Score
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RecommendationScoreBar
            score={final_score}
            size="large"
            sx={{ flex: 1 }}
          />
          {relation_type && (
            <ScoreChip score={final_score} label={relation_type} />
          )}
        </Box>
      </Box>

      {/* Base Scores */}
      {coreScores.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Base Scores
          </Typography>
          <Stack spacing={0.5}>
            {coreScores.map(({ key, label, value }) => (
              <ScoreRow key={key} label={label} value={value} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Weights */}
      {Object.keys(weights).length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Ensemble Weights
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {weights.cf_weight !== undefined && (
              <WeightChip label="CF" value={weights.cf_weight} />
            )}
            {weights.cb_weight !== undefined && (
              <WeightChip label="CB" value={weights.cb_weight} />
            )}
            {weights.kg_weight !== undefined && (
              <WeightChip label="KG" value={weights.kg_weight} />
            )}
          </Box>
        </Box>
      )}

      {/* Boosts */}
      {boostItems.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Applied Boosts
          </Typography>
          <Stack spacing={0.5}>
            {boostItems.map(({ key, label, value }) => (
              <BoostRow key={key} label={label} value={value} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Learned Bias from Override History */}
      {learned_bias.has_data && (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Learned from Overrides
          </Typography>
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
                {learned_bias.anchor_type} → {learned_bias.candidate_type}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="success.main">
                {learned_bias.approvals} approved
              </Typography>
              <Typography variant="caption" color="text.disabled">•</Typography>
              <Typography variant="caption" color="error.main">
                {learned_bias.rejections} rejected
              </Typography>
              {learned_bias.pair_bias !== 0 && (
                <>
                  <Typography variant="caption" color="text.disabled">→</Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{
                      color: learned_bias.pair_bias > 0 ? 'success.main' : 'error.main',
                    }}
                  >
                    {learned_bias.pair_bias > 0 ? '+' : ''}{formatNumber(learned_bias.pair_bias, { digits: 3 })} bias
                  </Typography>
                </>
              )}
            </Box>
            {(learned_bias.approvals + learned_bias.rejections) < 3 && (
              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                Need {3 - (learned_bias.approvals + learned_bias.rejections)} more decisions to influence scores
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {/* Override */}
      {override && (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Manual Override
          </Typography>
          <Typography variant="body2" color="primary.main">
            {override.decision}
            {override.weight_adjustment !== 0 && (
              <> ({override.weight_adjustment > 0 ? '+' : ''}{override.weight_adjustment})</>
            )}
          </Typography>
          {override.note && (
            <Typography variant="caption" color="text.secondary">
              {override.note}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
}

/**
 * Individual score row.
 */
function ScoreRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
        {label}
      </Typography>
      <RecommendationScoreBar
        score={value}
        size="small"
        showLabel
        sx={{ width: 120 }}
      />
    </Box>
  );
}

/**
 * Boost value row (can be negative or positive).
 */
function BoostRow({ label, value }) {
  const isPositive = value > 0;
  const displayValue = value > 0 ? `+${formatNumber(value)}` : formatNumber(value);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
          color: isPositive ? 'success.main' : value < 0 ? 'error.main' : 'text.secondary',
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
}

/**
 * Weight chip display.
 */
function WeightChip({ label, value }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="caption" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatPercent(value, { digits: 0 })}
      </Typography>
    </Box>
  );
}
